import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import VerifyEmail from "./components/auth/VerifyEmail";
import Nav from "./components/nav/Nav";
import Timesheets from "./components/Timesheets";
import { Forgot } from "./components/auth/Forgot";
import { Reset } from "./components/auth/Reset";
import { SetPassword } from "./components/auth/SetPassword";
import FirstLoginProfileModal from "./components/modal/FirstLoginProfileModal";
import Header from "./components/nav/Header";
import React, { useEffect, useState } from "react";
import { setAuth } from './redux/authSlice';
import { RootState } from './redux/store';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { SnackbarProvider } from 'notistack';
import Home from "./components/Home";
import Equipment from "./components/Equipment";
import Dashboard from "./components/Dashboard";
import TrainingPage from "./components/TrainingPage";
import TasksPage from "./components/TasksPage";
import DiskPage from "./components/diskComponents/DiskPage";
import Toil from "./components/Toil";
import HrPage from "./components/HrPage";
import { setCompanyTimeStep } from "./components/util/timeStep";
import './components/i18n/i18n';
import Finance from "./components/Finance";
import RightRail from "./components/rightPanel/RightRail";
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import NotFound from "./components/NotFound";
import Documents from "./components/Documents";
import Admin from "./components/Admin";
import Crm from "./components/Crm";
import useIsMobile from "./components/util/useIsMobile";
import MobileTabBar from "./components/mobile/MobileTabBar";
import MobileMore from "./components/mobile/MobileMore";
import MobileNotifications from "./components/mobile/MobileNotifications";
import MobileTimesheets from "./components/mobile/MobileTimesheets";
import MobileDashboard from "./components/mobile/MobileDashboard";
import MobileEquipment from "./components/mobile/MobileEquipment";
import MobileDocuments from "./components/mobile/MobileDocuments";
import MobileFinance from "./components/mobile/MobileFinance";
import MobileHr from "./components/mobile/MobileHr";
import MobileCrm from "./components/mobile/MobileCrm";
import MobileTraining from "./components/mobile/MobileTraining";
import MobileTasks from "./components/mobile/MobileTasks";

function ContentWrapper({ children, isMobile }: { children: React.ReactNode; isMobile: boolean }) {
  const location = useLocation();
  const isAuthPage = ['/login', '/register', '/forgot'].includes(location.pathname) || location.pathname.startsWith('/reset/') || location.pathname.startsWith('/verify-email/') || location.pathname.startsWith('/set-password/');
  const contentPad = isMobile
    ? 'wrapper-mobile px-[12px] pt-[10px] pb-[86px]'   // без левого рельса, место под нижнюю таб-панель
    : 'pl-[86px] pt-[10px] pb-[10px] pr-[10px]';
  return (
    <div className={`z-0 overflow-auto h-[94svh] mr-[-5px] wrapper-all ${isAuthPage ? 'p-0' : contentPad}`}>
      {children}
    </div>
  );
}

function App() {
  const auth = useSelector((state: RootState) => state.auth.value);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [userInfo, setUserInfo] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [companyID, setCompanyID] = useState('');
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | string[]>([]);
  const [staticDateCompany, setStaticDateCompany] = useState('');
  const [staticDateSelect, setStaticDateSelect] = useState('');
  const [userNotice, setUserNotice] = useState([]);
  const [urlImage, setUrlImage] = useState('');
  const [urlLogo, setUrlLogo] = useState('');
  const [unit, setUnit] = useState('');
  const [timeZone, setTimeZone] = useState<string | null>(null);
  const [accessRole, setAccessRole] = useState<Record<string, any>>({});
  const [fullUser, setFullUser] = useState<any>(null);
  const [profileModalDismissed, setProfileModalDismissed] = useState(() => sessionStorage.getItem('profileModalDismissed') === '1');
  const [is24HourFormat, setIs24HourFormat] = useState(() => {
    const storedFormat = localStorage.getItem('is24HourFormat');
    return storedFormat ? JSON.parse(storedFormat) : true;
  });
  const [taskOrTasks, setTaskOrTasks] = useState(true);
  const [baseUrl, setBaseUrl] = useState('https://timeberlo.com:8089/');
  const getLanguage = () => {
    let language = localStorage.getItem('language');
    if (!language) {
      language = 'en';
      localStorage.setItem('language', language);
    }
    return language;
  };
  const [lng, setLng] = useState(getLanguage);
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const storedTheme = localStorage.getItem('darkTheme');
    return storedTheme ? JSON.parse(storedTheme) : false;
  });

  const DARK_THEMES = ['dark', 'midnight', 'slate', 'carbon'];
  const THEME_CLASSES = ['tb-ocean','tb-warm','tb-forest','tb-lavender','tb-midnight','tb-slate','tb-carbon'];

  const [colorTheme, setColorTheme] = useState<string>(() =>
    localStorage.getItem('tb_theme') || 'light'
  );

  const applyColorTheme = (themeId: string) => {
    // Remove all theme classes
    THEME_CLASSES.forEach(c => document.body.classList.remove(c));
    // Add new theme class if not base light/dark
    if (themeId !== 'light' && themeId !== 'dark') {
      document.body.classList.add(`tb-${themeId}`);
    }
    // Apply dark-theme class for dark variants
    const isDark = DARK_THEMES.includes(themeId);
    document.body.classList.toggle('dark-theme', isDark);
    localStorage.setItem('darkTheme', JSON.stringify(isDark));
    localStorage.setItem('tb_theme', themeId);
    setIsDarkTheme(isDark);
    setColorTheme(themeId);
  };

  // Apply saved color theme on mount
  useState(() => {
    const saved = localStorage.getItem('tb_theme') || 'light';
    if (saved !== 'light') applyColorTheme(saved);
  });
  const { enqueueSnackbar } = useSnackbar();

  // Function to merge multiple roles into one object
  const mergeRoles = (roles: any[]) => {
    if (!roles || roles.length === 0) {
      return {};
    }
    // Collect all role IDs
    const roleIds = roles.map(role => role.id).filter(id => id !== undefined && id !== null);
    // Collect all role colors
    const roleColors = roles.map(role => role.color).filter(color => color !== undefined && color !== null);
    // Check if any role has description === -1
    const hasAdminRole = roles.some(role => role.description === -1);
    // Start with the first role as base
    const merged: Record<string, any> = { ...roles[0] };
    // Replace id with array of all role IDs
    merged.id = roleIds;
    // Store colors array
    merged.colors = roleColors;
    // If any role has description === -1, set description to -1
    if (hasAdminRole) {
      merged.description = -1;
    }
    // Iterate through all roles and merge boolean and numeric fields
    roles.forEach((role) => {
      Object.keys(role).forEach((key) => {
        // Skip id, color, and description fields as we already handled them
        if (key === 'id' || key === 'color' || key === 'description') {
          return;
        }
        const value = role[key];
        // For boolean fields: if any role has true, set to true
        if (typeof value === 'boolean') {
          if (value === true) {
            merged[key] = true;
          } else if (merged[key] !== true) {
            merged[key] = merged[key] || false;
          }
        }
        // For numeric fields: take the maximum value (or first non-null value)
        else if (typeof value === 'number' && value !== null) {
          if (merged[key] === null || merged[key] === undefined) {
            merged[key] = value;
          } else {
            merged[key] = Math.max(merged[key], value);
          }
        }
        // For other fields, keep the first role's value
        else if (merged[key] === undefined || merged[key] === null) {
          merged[key] = value;
        }
      });
    });

    return merged;
  };

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      setBaseUrl('http://localhost:8000/');
    } else {
      setBaseUrl('https://timeberlo.com:8089/');
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if ((window.location.pathname.startsWith(`${process.env.PUBLIC_URL || ''}/adminpanel`) || window.location.pathname.startsWith("/adminpanel"))) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Restore token from localStorage if it's not in headers
        if (!axios.defaults.headers.common['Authorization']) {
          const savedToken = localStorage.getItem('authToken');
          if (savedToken) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          } else {
            dispatch(setAuth(false));
            setLoading(false);
            return;
          }
        }
        const { data } = await axios.get('user');
        if (data) {
          setFullUser(data);
          setTimeZone(data.company.zone_description);
          if (data.company.static_date !== null) {
            setStaticDateCompany(data.company.static_date.format_string.toUpperCase())
          } else {
            setStaticDateCompany('YYYY-MM-DD');
          }
          setCompanyTimeStep(data.company.hr_timeStepMinutes);
          setUserNotice(data.notice);
          // Merge all roles into one object (primary role always included)
          const allRoles = data.role
            ? [data.role, ...(data.roles ?? []).filter((r: any) => r.id !== data.role.id)]
            : (data.roles ?? []);
          const mergedRoles = allRoles.length > 0 ? mergeRoles(allRoles) : (data.role ?? {});
          setAccessRole(mergedRoles);
          // Set userRole as array of colors or single color
          if (mergedRoles && mergedRoles.colors && mergedRoles.colors.length > 0) {
            setUserRole(mergedRoles.colors);
          } else if (data.role && data.role.color) {
            setUserRole([data.role.color]);
          } else {
            setUserRole(['#888888']);
          }
          setUserInfo(`${data.first_name} ${data.last_name}`);
          if (data.email) setUserEmail(data.email);
          {
            const urlBase = window.location.hostname === 'localhost'
              ? 'http://localhost:8000/'
              : 'https://timeberlo.com:8089/';
            setUrlImage(data.avatarUrl ? `${urlBase}${data.avatarUrl}` : '');
          }
          setUnit(data.company.unit);
          setTaskOrTasks(data.company.task_single);
          setCompanyID(data.company.id);
          dispatch(setAuth(true));
        }
      } catch (error: any) {
        console.error('Error fetching user data:', error);
        // If 401 error, remove token
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          delete axios.defaults.headers.common['Authorization'];
        }
        dispatch(setAuth(false));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth, dispatch]);

  useEffect(() => {
    if (!auth) return;
    (async () => {
      try {
        const { data } = await axios.get('user');
        if (data.avatarUrl) {
          setUrlImage(`${baseUrl}${data.avatarUrl}`);
        }
        if (data.company.logoUrl) {
          setUrlLogo(`${baseUrl}${data.company.logoUrl.replace(/^\//, '')}`);
        }
      } catch (e) { }
    })();
  }, [auth, urlImage, urlLogo, baseUrl]);

  const refreshUserHeder = async () => {
    try {
      const { data } = await axios.get('user');
      if (data) {
        setUserNotice(data.notice);
        setUserInfo(`${data.first_name} ${data.last_name}`);
        if (data.avatarUrl) {
          setUrlImage(`${baseUrl}${data.avatarUrl}`);

        } else {
          setUrlImage('');
        }
        if (data.company.logoUrl) {
          setUrlLogo(`${baseUrl}${data.company.logoUrl.replace(/^\//, '')}`);
        } else {
          setUrlLogo('');
        }
        // Set userRole as array of colors or single color
        if (data.roles && data.roles.length > 0) {
          const colors = data.roles.map((role: any) => role.color).filter((color: any) => color !== undefined && color !== null);
          setUserRole(colors.length > 0 ? colors : ['#888888']);
        } else if (data.role && data.role.color) {
          setUserRole([data.role.color]);
        } else {
          setUserRole(['#888888']);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const refreshRoleAccept = async () => {
    try {
      const { data } = await axios.get('user');
      if (data) {
        // Merge all roles into one object (primary role always included)
        const allRoles2 = data.role
          ? [data.role, ...(data.roles ?? []).filter((r: any) => r.id !== data.role.id)]
          : (data.roles ?? []);
        const mergedRoles = allRoles2.length > 0 ? mergeRoles(allRoles2) : (data.role ?? {});
        setAccessRole(mergedRoles);
        // Update userRole colors as well
        if (mergedRoles && mergedRoles.colors && mergedRoles.colors.length > 0) {
          setUserRole(mergedRoles.colors);
        } else if (data.role && data.role.color) {
          setUserRole([data.role.color]);
        } else {
          setUserRole(['#888888']);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleTimeFormatChange = () => {
    const newFormat = !is24HourFormat;
    setIs24HourFormat(newFormat);
    localStorage.setItem('is24HourFormat', JSON.stringify(newFormat));
  };

  const handleTaskOrTasks = async () => {
    try {
      await axios.put(`/company/${companyID}/`, {
        task_single: !taskOrTasks
      });
      setTaskOrTasks(!taskOrTasks);
      enqueueSnackbar('Change task single', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Error`, { variant: 'error' });
    }
  };

  const isAdminRoute = (window.location.pathname.startsWith(`${process.env.PUBLIC_URL || ''}/adminpanel`) || window.location.pathname.startsWith("/adminpanel"));

  return (
    <SnackbarProvider maxSnack={3} autoHideDuration={3000} className="opacity-80">
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        {!isAdminRoute ? (
          <>
            {/* Mesh gradient background */}
            <div className="mesh-bg" />
            <div className="mesh-noise" />
            {!isMobile && <Nav access={accessRole} />}
            <div className={`w-full relative overflow-hidden container-body'}`}>
              <Header
                isDarkTheme={isDarkTheme}
                setIsDarkTheme={setIsDarkTheme}
                userInfo={userInfo}
                userRole={userRole}
                refreshUserHeder={refreshUserHeder}
                lng={lng}
                setLng={setLng}
                userNotice={userNotice}
                urlImage={urlImage}
                baseUrl={baseUrl}
                is24HourFormat={is24HourFormat}
                timeZone={timeZone}
                urlLogo={urlLogo}
              />
              {auth && !isMobile && (
                <RightRail
                  userNotice={userNotice}
                  refreshUserHeder={refreshUserHeder}
                  colorTheme={colorTheme}
                  onThemeChange={applyColorTheme}
                  userEmail={userEmail}
                  onLogout={async () => { try { await axios.post('logout', {}, { withCredentials: true }); } catch {} finally { axios.defaults.headers.common['Authorization'] = ''; localStorage.removeItem('authToken'); dispatch(setAuth(false)); window.location.replace(`${process.env.PUBLIC_URL}/login`); } }}
                  settingsProps={{
                    access: accessRole,
                    baseUrl,
                    refreshUserHeder,
                    is24HourFormat,
                    handleTimeFormatChange,
                    taskOrTasks,
                    handleTaskOrTasks,
                    timeZone,
                    setTimeZone,
                    staticDateSelect,
                    setStaticDateSelect,
                    unit,
                    setUnit,
                  }}
                />
              )}
              {auth && fullUser && fullUser.profile_completed === false && !profileModalDismissed && (
                <FirstLoginProfileModal
                  user={fullUser}
                  baseUrl={baseUrl}
                  onRemindLater={() => { sessionStorage.setItem('profileModalDismissed', '1'); setProfileModalDismissed(true); }}
                  onSaved={() => { setFullUser((p: any) => p ? { ...p, profile_completed: true } : p); refreshUserHeder(); }}
                />
              )}
              <ContentWrapper isMobile={isMobile}>
                {!loading ? (
                  <Routes>
                    <Route path="/" element={!auth ? <Home /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={!auth ? <Login /> : (isMobile
                      ? <MobileDashboard access={accessRole} baseUrl={baseUrl} />
                      : <Dashboard access={accessRole} baseUrl={baseUrl} urlLogo={urlLogo} />)} />
                    <Route path="/timesheets" element={!auth ? <Login /> : (isMobile
                      ? <MobileTimesheets access={accessRole} taskOrTasks={taskOrTasks} refreshUserHeder={refreshUserHeder} />
                      : <Timesheets
                      access={accessRole}
                      is24HourFormat={is24HourFormat}
                      taskOrTasks={taskOrTasks}
                      staticDateCompany={staticDateCompany}
                      refreshUserHeder={refreshUserHeder}
                      baseUrl={baseUrl} />)} />
                    <Route path="/docs" element={!auth ? <Login /> : (isMobile
                      ? <MobileDocuments access={accessRole} baseUrl={baseUrl} />
                      : <Documents
                      access={accessRole}
                      baseUrl={baseUrl} />)} />
                    <Route path="/equipment" element={!auth ? <Login /> : (isMobile ? <MobileEquipment access={accessRole} /> : <Equipment access={accessRole} />)} />
                    <Route path="/training" element={!auth ? <Login /> : (isMobile ? <MobileTraining access={accessRole} /> : <TrainingPage access={accessRole} baseUrl={baseUrl} />)} />
                    <Route path="/disk" element={!auth ? <Login /> : <DiskPage access={accessRole} baseUrl={baseUrl} />} />
                    <Route path="/finance" element={!auth ? <Login /> : (isMobile ? <MobileFinance access={accessRole} /> : <Finance
                      access={accessRole}
                      is24HourFormat={is24HourFormat}
                      staticDateCompany={staticDateCompany}
                      baseUrl={baseUrl}
                      unit={unit} />)} />
                    <Route path="/users" element={<Navigate to="/hr" replace />} />
                    <Route path="/hr" element={!auth ? <Login /> : (isMobile ? <MobileHr access={accessRole} baseUrl={baseUrl} /> : <HrPage
                      refreshUserHeder={refreshUserHeder}
                      refreshRoleAccept={refreshRoleAccept}
                      access={accessRole}
                      staticDateCompany={staticDateCompany}
                      baseUrl={baseUrl} />)} />
                    <Route path="/toil" element={!auth ? <Login /> : <Toil access={accessRole} baseUrl={baseUrl} />} />
                    <Route path="/crm" element={!auth ? <Login /> : (isMobile ? <MobileCrm access={accessRole} /> : <Crm access={accessRole} />)} />
                    <Route path="/tasks" element={!auth ? <Login /> : (isMobile ? <MobileTasks access={accessRole} /> : <TasksPage access={accessRole} />)} />
                    <Route path="/more" element={!auth ? <Login /> : (isMobile ? <MobileMore access={accessRole} userInfo={userInfo} userEmail={userEmail} /> : <Navigate to="/dashboard" replace />)} />
                    <Route path="/notifications" element={!auth ? <Login /> : (isMobile ? <MobileNotifications notices={userNotice} /> : <Navigate to="/dashboard" replace />)} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot" element={<Forgot />} />
                    <Route path="/reset/:token" element={<Reset />} />
                    <Route path="/set-password/:token" element={<SetPassword />} />
                    <Route path="/verify-email/:token" element={<VerifyEmail />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                  </Box>
                )}
              </ContentWrapper>
              {auth && isMobile && <MobileTabBar access={accessRole} />}
            </div>
          </>
        ) : (
          <Admin baseUrl={baseUrl} isDarkTheme={isDarkTheme} setIsDarkTheme={setIsDarkTheme} />
        )}
      </BrowserRouter>
    </SnackbarProvider>
  );
}

export default App;